import { useState } from 'react';
import { Grid, TextField, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { toast } from 'react-toastify';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { createAutojob, createAutojob2, CreateAutojob2Request } from 'src/shared/services/autojobService';
import { FrequencyType, frequencyTypeOptions, frequencyTypeToScheduleType } from './createAutojobWithSelectionAndFrequencyModal';
import { versionOptions } from './createAutojobModal';
import { autojobTypeOptions, SnapshotJobType } from './selectAutojobTypeModal';

// Assembly per jobbtyp och version.
const assemblyNames: Record<SnapshotJobType, Record<1 | 2, string>> = {
  deleteSnapshot: { 1: 'YvnTaskPluginAutojob|YvnTaskPluginAutojob.DeleteSnapshots', 2: 'DeleteSnapshots' },
  anonymizeSnapshot: { 1: 'YvnTaskPluginAutojob|YvnTaskPluginAutojob.AnonymizeSnapshots', 2: 'AnonymizeSnapshots' },
};

type CreateAutojobForMemberSnapshotModalProps = {
  open: boolean;
  jobType: SnapshotJobType;
  onClose: () => void;
  onSaved: () => void;
};

export const CreateAutojobForMemberSnapshotModal = (props: CreateAutojobForMemberSnapshotModalProps) => {
  const { open, jobType, onClose, onSaved } = props;
  const { t } = useTranslation(['settings', 'common']);
  const [version, setVersion] = useState<1 | 2>(2);
  const [olderThanMonths, setOlderThanMonths] = useState(0);
  // Årskopior är bara relevant när jobbet raderar ögonblicksbilder.
  const [saveYearlyCopies, setSaveYearlyCopies] = useState(false);
  const [frequency, setFrequency] = useState(1);
  const [frequencyType, setFrequencyType] = useState<FrequencyType>('day');
  const [startDate, setStartDate] = useState<Dayjs | null>(() => dayjs().add(0, 'minute'));
  const [startTime, setStartTime] = useState<Dayjs | null>(() => dayjs().add(0, 'minute'));

  const [saving, setSaving] = useState(false);

  const jobTypeLabelKey = autojobTypeOptions.find((opt) => opt.value === jobType)?.labelKey;

  const handleSave = async () => {
    if (saving) return;
    const assemblyName = assemblyNames[jobType][version];
    if (!startDate?.isValid() || !startTime?.isValid()) return;

    const request: CreateAutojob2Request = {
      taskName: jobTypeLabelKey ? t(jobTypeLabelKey) : jobType,
      assemblyName,
      scheduleType: frequencyTypeToScheduleType[frequencyType],
      increaseNum: frequency,
      startTimeStamp: startDate.hour(startTime.hour()).minute(startTime.minute()).format('YYYY-MM-DDTHH:mm'),
      custom1: '',
      custom2: String(olderThanMonths),
      custom3: jobType === 'deleteSnapshot' ? String(saveYearlyCopies) : '',
      custom4: '',
      custom5: '',
      custom6: '',
      custom7: '',
      custom8: '',
      custom9: '',
      custom10: '',
      custom11: '',
      custom12: '',
    };

    setSaving(true);
    try {
      await (version === 1 ? createAutojob(request) : createAutojob2(request));
      toast.success(<>{t('common:statusMessages.saveChangesSuccess')}</>);
      onSaved();
    } catch {
      toast.error(<>{t('common:statusMessages.saveChangesError')}</>);
    } finally {
      setSaving(false);
    }
  };

  return (
    <GenericDialog
      open={open}
      dialogTitle={t('settings:autojobs.createModal.title')}
      dialogSubTitle={jobTypeLabelKey ? t(jobTypeLabelKey) : undefined}
      maxWidth='sm'
      onClose={onClose}
      onSave={handleSave}
      saveLabel='common:button.save'
      abortLabel='common:button.cancel'
      dialogContent={
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={t('settings:autojobs.createModal.version')}
              value={version}
              onChange={(e) => setVersion(Number(e.target.value) as 1 | 2)}
              fullWidth
              size='small'
              select
            >
              {versionOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('settings:autojobs.snapshotModal.olderThanMonths')}
              value={olderThanMonths}
              onChange={(e) => setOlderThanMonths(Math.max(0, Math.floor(Number(e.target.value))))}
              fullWidth
              size='small'
              type='number'
              inputProps={{ min: 0, step: 1 }}
            />
          </Grid>
          {jobType === 'deleteSnapshot' && (
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={saveYearlyCopies} onChange={(e) => setSaveYearlyCopies(e.target.checked)} />}
                label={t('settings:autojobs.snapshotModal.saveYearlyCopies')}
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <TextField
              label={t('settings:autojobs.selectionModal.frequency')}
              value={frequency}
              onChange={(e) => setFrequency(Math.max(1, Math.floor(Number(e.target.value))))}
              fullWidth
              size='small'
              type='number'
              inputProps={{ min: 1, step: 1 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('settings:autojobs.selectionModal.frequencyType')}
              value={frequencyType}
              onChange={(e) => setFrequencyType(e.target.value as FrequencyType)}
              fullWidth
              size='small'
              select
            >
              {frequencyTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <DatePicker
              label={t('settings:autojobs.selectionModal.startDate')}
              value={startDate}
              onChange={(val) => setStartDate(val)}
              format='YYYY-MM-DD'
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid item xs={6}>
            <TimePicker
              label={t('settings:autojobs.selectionModal.startTime')}
              value={startTime}
              onChange={(val) => setStartTime(val)}
              ampm={false}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
        </Grid>
      }
    />
  );
};
